import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Badge, Button, Icon, Link, Modal, Spinner, Text } from "~/components-shared";
import eventsActionCreators from "~/store/events/actions";
import { resolveDates } from "~/lib/helpers";
import styled, { useTheme } from "styled-components";
import toastsActionCreators from "~/store/toasts/actions";
import Markdown from "~/components/Markdown";
import { CRUDActionEnum, EventSourceEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useHistory } from "react-router-dom";
import lists from "~/lib/lists";
import eventSelectors from "~/store/events/selectors";
const Attendees = styled.div`
  display: flex;
  flex-direction: column;
`;
const GoogleEventReadModal = _ref => {
  let {
    event,
    onClose
  } = _ref;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const actions = [];
  const history = useHistory();
  const importStatus = useSelector(eventSelectors.getImportStatus);
  const error = useSelector(eventSelectors.getErrorByEvent("+"));
  const handleJoinVideoCall = () => {
    if (event?.videoLink) {
      window.open(event.videoLink, "_blank");
    }
  };
  const handleImportToJoyn = () => {
    if (event) {
      dispatch(eventsActionCreators.importStarted(EventSourceEnum.GOOGLE, event.id, _ref2 => {
        let {
          data: newEvent
        } = _ref2;
        if (newEvent) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("GoogleEventReadModal.eventImported", {
              name: newEvent.name
            }),
            timeout: 10000,
            actions: [{
              icon: "external-link",
              onClick: () => history.push(RoutesEnum.EVENT.replace(":eventId", newEvent.id))
            }]
          }));
          dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.READ, newEvent.id));
          onClose();
        } else {
          toastsActionCreators.pushed({
            severity: "danger",
            message: t("GoogleEventReadModal.importFailed"),
            timeout: 10000
          });
        }
      }));
    }
  };
  const handleCopyLink = () => {
    if (event?.googleCalendarLink) {
      const type = "text/plain";
      const blob = new Blob([event.googleCalendarLink], {
        type
      });
      const data = [new ClipboardItem({
        [type]: blob
      })];
      navigator.clipboard.write(data).then(() => {
        dispatch(toastsActionCreators.pushed({
          message: t("EventReadModal.copiedToClipboard"),
          timeout: 3000
        }));
      });
    }
  };
  const handleView = () => {
    window.open(event?.googleCalendarLink);
  };
  actions.push({
    icon: "share",
    tip: t("EventReadModal.copyEventLink"),
    onClick: handleCopyLink
  });
  actions.push({
    icon: "eye",
    tip: t("common.actions.view"),
    onClick: handleView
  });
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: event?.name,
    onClose: onClose,
    actions: actions,
    maxWidth: "600px",
    fullWidth: true
  }, event ? /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventForm.type")), /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 5
    },
    display: "inline-block"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.events.types.colors(theme)[event.type]
  }, lists.events.types.names[event.type])), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventForm.date")), /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 5
    },
    display: "inline-block"
  }, resolveDates(event.dateFrom, event.dateTo)), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventForm.description")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    s: {
      mb: 5
    }
  }, event.description ? /*#__PURE__*/React.createElement(Markdown, {
    text: event.description
  }) : "-"), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("GoogleEventReadModal.attendees"), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary",
    fontSize: 3.5,
    s: {
      ml: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: theme.colors.text.secondary,
    scale: 3
  }), " ", event.attendees.length)), event.attendees.length ? /*#__PURE__*/React.createElement(Attendees, null, event.attendees.map(_ref3 => {
    let {
      email
    } = _ref3;
    return /*#__PURE__*/React.createElement(Link, {
      key: email,
      href: `mailto:${email}`
    }, email);
  })) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 7
    },
    display: "inline-block",
    color: "textSecondary"
  }, t("GoogleEventReadModal.noAttendees"))) : /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: onClose,
    rounded: true
  }, t("common.actions.close")), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    loading: importStatus === StatusStateEnum.LOADING,
    onClick: handleImportToJoyn,
    rounded: true
  }, t("GoogleEventReadModal.importToJoyn")), event?.videoLink && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleJoinVideoCall,
    s: {
      ml: 2,
      px: 8
    },
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "videocam",
      s: {
        mr: 2
      }
    }),
    rounded: true
  }, t("EventReadModal.joinVideoCall"))));
};
export default GoogleEventReadModal;